import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiTarget, FiUsers, FiStar } = FiIcons;

const About = () => {
  return (
    <section id="about" className="py-20 bg-white/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent">
              A New Kind of
            </span>
            <br />
            <span className="text-gray-800">Creative Experience</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We've reimagined the paint and sip experience by weaving together mindfulness, 
            life coaching, and artistic expression into something truly transformative.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=600&h=400&fit=crop"
                alt="Cozy art studio with people painting mindfully"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-2xl shadow-xl">
                <SafeIcon icon={FiHeart} className="h-8 w-8 text-rose-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h3 className="text-3xl font-bold text-gray-800 mb-6">
              Where Art Meets Inner Growth
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our sessions begin with guided mindfulness practices to help you connect with your inner self. 
              We then lead you through visualization exercises to discover your personal "happy place" - 
              the peaceful sanctuary that exists within your mind.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              As you paint this vision onto canvas, our certified life coaches guide you through gentle 
              conversations about personal growth, mental fitness (PQ), and self-discovery. It's therapy, 
              art class, and personal development all rolled into one beautiful experience.
            </p>
            
            <div className="grid grid-cols-2 gap-4 mt-8">
              {[
                { number: "500+", label: "Lives Transformed" },
                { number: "98%", label: "Feel More Centered" },
                { number: "4.9/5", label: "Average Rating" },
                { number: "3 Years", label: "Of Experience" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="text-center p-4 bg-gradient-to-br from-rose-50 to-purple-50 rounded-xl"
                >
                  <div className="text-2xl font-bold text-gray-800">{stat.number}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Our Approach */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-rose-50 to-purple-50 rounded-3xl p-8 md:p-12"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-800">Our Unique Approach</h3>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                icon: FiTarget,
                title: "Mindful Preparation",
                desc: "Begin with breathing exercises and meditation to center yourself"
              },
              {
                icon: FiHeart,
                title: "Visualization Journey",
                desc: "Guided imagery to discover your personal happy place"
              },
              {
                icon: FiStar,
                title: "Intuitive Creation",
                desc: "Paint your vision while exploring personal insights"
              },
              {
                icon: FiUsers,
                title: "Gentle Integration",
                desc: "Reflect on your experience and newfound clarity"
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -5 }}
                className="text-center"
              >
                <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <SafeIcon icon={step.icon} className="h-8 w-8 text-rose-500" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">{step.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;